import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { Session, User } from '@supabase/supabase-js'
import type { Database } from '@jac/shared/generated/db-types'
import { supabase } from '@/lib/supabase'
import { resolveMfaStatus } from '@/lib/mfa'

type Role = Database['public']['Enums']['user_role']

// The company scope of a CLIENT-portal user (external client). A person (one email + optional phone)
// with read access to one-or-more companies; `isAdmin` per company = sees all that company's docs.
export type ClientCompany = {
  clientId: string
  name: string
  nif: string
  isAdmin: boolean
  // Per-client "Contabilidade analítica" product gate: etiquetas/regras/wizard
  // surfaces render only when true (staff toggles it in Admin › Clientes).
  analyticalAccounting: boolean
  // Per-client gate for Roberto, the document Q&A assistant: the portal's
  // floating launcher renders only when true (staff toggles it in Admin ›
  // Clientes). Independent of analyticalAccounting — neither switches the
  // other on.
  assistantEnabled: boolean
}
export type ClientScope = {
  email: string
  phone: string | null
  companies: ClientCompany[]
}

// A person may hold SEVERAL principals — staff of one firm, client-portal user of another — and
// exactly one is active at a time. `consultants` is a membership table now (one row per person+firm),
// so "the consultant" is whichever membership is currently active, not "the person's row".
export type StaffPrincipal = {
  kind: 'staff'
  consultantId: string
  firmId: string
  firmName: string
  role: Role
  fullName: string
  carteiraId: string | null
}
export type ClientPrincipal = {
  kind: 'client'
  firmId: string
  firmName: string
  companies: ClientCompany[]
}
// The platform OPERATOR entering a tenant for support. Not a membership — it is granted per person
// in `platform_admins`, and entering a firm writes an audit event.
export type PlatformPrincipal = {
  kind: 'platform'
  firmId: string
  firmName: string
}
export type Principal = StaffPrincipal | ClientPrincipal | PlatformPrincipal

export function principalKey(p: Principal): string {
  if (p.kind === 'staff') return `staff:${p.consultantId}`
  if (p.kind === 'platform') return `platform:${p.firmId}`
  return `client:${p.firmId}`
}
export function principalLabel(p: Principal): string {
  if (p.kind === 'staff') return p.firmName
  if (p.kind === 'platform') return `${p.firmName} · plataforma`
  return `${p.firmName} · cliente`
}

// Where a principal's area lives. Switching kinds must be followed by a navigate() here —
// staying put leaves the user on a route the new principal can't enter, and the guard
// (RequireRole/RequireClient) re-renders the picker they just answered.
export function principalHome(p: Principal): string {
  // Portal users land on Início — the page that frames the period — not straight in the table.
  return p.kind === 'client' ? '/portal/inicio' : '/operacoes'
}

// The active staff membership, narrowed to what the UI reads. Field names stay snake_case so the
// existing `session.consultant.role` / `.full_name` / `.firm_id` call sites keep working.
export type ActiveConsultant = {
  id: string
  user_id: string
  full_name: string
  role: Role
  firm_id: string
  firm_name: string
  carteira_id: string | null
}

// Terminal states the UI cares about. The MFA gate keys on the ACTIVE principal, not on "does a
// consultants row exist": staff go through TOTP (CLAUDE.md red line), external CLIENT-portal users
// log in with magic-link only — an explicit, documented exception scoped to client principals. A
// person holding both therefore only meets TOTP when they switch into their staff principal.
export type SessionState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'needs_mfa_enroll'; user: User; session: Session }
  | { status: 'needs_mfa_challenge'; user: User; session: Session; factorId: string }
  | { status: 'unprovisioned'; user: User; session: Session }
  | { status: 'needs_principal_choice'; user: User; session: Session; principals: Principal[] }
  | {
      status: 'authenticated'
      user: User
      session: Session
      consultant: ActiveConsultant
      principals: Principal[]
    }
  | {
      status: 'authenticated_client'
      user: User
      session: Session
      client: ClientScope
      principals: Principal[]
    }

// ── f_my_principals() payload ────────────────────────────────────────────────────────────────────
type PrincipalsPayload = {
  user_id: string | null
  is_platform_admin: boolean
  platform_firms: Array<{ firm_id: string; firm_name: string }>
  active: {
    kind: 'staff' | 'client' | 'platform'
    consultant_id: string | null
    firm_id: string
  } | null
  staff: Array<{
    consultant_id: string
    firm_id: string
    firm_name: string
    role: Role
    full_name: string
    carteira_id: string | null
  }>
  client: Array<{
    firm_id: string
    firm_name: string
    companies: Array<{
      client_id: string
      name: string
      nif: string
      is_admin: boolean
      analytical_accounting?: boolean
      assistant_enabled?: boolean
    }>
  }>
}

function toPrincipals(p: PrincipalsPayload): Principal[] {
  const staff: Principal[] = (p.staff ?? []).map((s) => ({
    kind: 'staff',
    consultantId: s.consultant_id,
    firmId: s.firm_id,
    firmName: s.firm_name,
    role: s.role,
    fullName: s.full_name,
    carteiraId: s.carteira_id,
  }))
  const client: Principal[] = (p.client ?? []).map((c) => ({
    kind: 'client',
    firmId: c.firm_id,
    firmName: c.firm_name,
    companies: (c.companies ?? []).map((co) => ({
      clientId: co.client_id,
      name: co.name,
      nif: co.nif,
      isAdmin: co.is_admin,
      analyticalAccounting: co.analytical_accounting ?? false,
      assistantEnabled: co.assistant_enabled ?? false,
    })),
  }))
  const platform: Principal[] = (p.platform_firms ?? []).map((f) => ({
    kind: 'platform',
    firmId: f.firm_id,
    firmName: f.firm_name,
  }))
  return [...staff, ...client, ...platform]
}

async function fetchPrincipals(): Promise<PrincipalsPayload | null> {
  const { data, error } = await supabase.rpc('f_my_principals' as never)
  if (error) {
    console.error('[useSession] f_my_principals failed', error.message)
    return null
  }
  return data as unknown as PrincipalsPayload
}

// Standalone auto-provisioning (match `clients.contact_emails`) lives in the edge function, so a
// brand-new portal person has no principals until it runs. Only called when the RPC came back empty.
async function tryAutoProvisionClient(): Promise<boolean> {
  const { data, error } = await supabase.functions.invoke('client-portal-provision', { body: {} })
  if (error) {
    console.warn('[useSession] client-portal-provision failed', error.message)
    return false
  }
  const res = data as { companies?: unknown[] } | { unresolved: true } | { staff: true } | null
  return !!res && 'companies' in res && Array.isArray(res.companies) && res.companies.length > 0
}

// email/phone are person-level and drive the portal's own-uploads attribution.
//
// The `.eq('id', userId)` is not redundant with RLS. Without it PostgREST sends
// `select email, phone from client_users limit 1` and leaves the narrowing entirely to the
// policies — so Postgres walks the table evaluating all five of them per row, including
// `client_users_clientadmin_read`, whose EXISTS calls the SECURITY DEFINER is_client_admin_of()
// once per company link, until one row passes. Measured on production over the 24h to
// 2026-09-11: 457 calls, p50 939 ms, p95 1.28 s — on a 224-row table, for one row the caller
// already knows the primary key of. With the filter it is an index lookup and the policies see a
// single row. RLS decides what you may read; it is not a query plan.
async function fetchClientIdentity(
  userId: string,
): Promise<{ email: string; phone: string | null } | null> {
  const { data } = await supabase
    .from('client_users' as never)
    .select('email, phone')
    .eq('id', userId)
    .maybeSingle()
  return (data as { email: string; phone: string | null } | null) ?? null
}

type SessionContextValue = {
  state: SessionState
  principals: Principal[]
  active: Principal | null
  isPlatformAdmin: boolean
  switchTo: (p: Principal) => Promise<boolean>
  refresh: () => void
}

const SessionContext = createContext<SessionContextValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: 'loading' })
  const [principals, setPrincipals] = useState<Principal[]>([])
  const [active, setActive] = useState<Principal | null>(null)
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false)
  const [nonce, setNonce] = useState(0)
  const refresh = useCallback(() => setNonce((n) => n + 1), [])

  useEffect(() => {
    let cancelled = false

    async function hydrate(session: Session | null) {
      if (!session) {
        if (!cancelled) {
          setState({ status: 'unauthenticated' })
          setPrincipals([])
          setActive(null)
        }
        return
      }

      let payload = await fetchPrincipals()
      if (cancelled) return

      setIsPlatformAdmin(!!payload?.is_platform_admin)
      let list = payload ? toPrincipals(payload) : []
      if (list.length === 0) {
        // Might be a first-time portal person — let the edge function try to resolve them, then ask
        // again rather than trusting its (differently shaped) response.
        if (await tryAutoProvisionClient()) {
          payload = await fetchPrincipals()
          list = payload ? toPrincipals(payload) : []
        }
      }
      if (cancelled) return

      if (list.length === 0) {
        setPrincipals([])
        setActive(null)
        setState({ status: 'unprovisioned', user: session.user, session })
        return
      }

      // Which principal is live: the server's explicit choice, else the only one they hold. With
      // several and no choice we must ASK — picking arbitrarily is the bug this all exists to fix.
      const chosen =
        list.find((p) => {
          const a = payload?.active
          if (!a) return false
          if (a.kind === 'staff') return p.kind === 'staff' && p.consultantId === a.consultant_id
          if (a.kind === 'platform') return p.kind === 'platform' && p.firmId === a.firm_id
          return p.kind === 'client' && p.firmId === a.firm_id
        }) ?? (list.length === 1 ? list[0] : undefined)

      setPrincipals(list)

      if (!chosen) {
        setActive(null)
        setState({
          status: 'needs_principal_choice',
          user: session.user,
          session,
          principals: list,
        })
        return
      }
      setActive(chosen)

      if (chosen.kind === 'staff' || chosen.kind === 'platform') {
        // STAFF path — MFA gate (red line).
        let mfa
        try {
          mfa = await resolveMfaStatus()
        } catch {
          if (!cancelled) setState({ status: 'unauthenticated' })
          return
        }
        if (cancelled) return
        if (mfa.kind === 'enroll_required') {
          setState({ status: 'needs_mfa_enroll', user: session.user, session })
          return
        }
        if (mfa.kind === 'challenge_required') {
          setState({
            status: 'needs_mfa_challenge',
            user: session.user,
            session,
            factorId: mfa.factorId,
          })
          return
        }
        setState({
          status: 'authenticated',
          user: session.user,
          session,
          principals: list,
          // A platform admin holds no membership, so there is no consultants row to describe them.
          // Inside a firm they entered they act with admin rights — current_role() says the same
          // thing server-side, so the UI and RLS agree.
          consultant:
            chosen.kind === 'staff'
              ? {
                  id: chosen.consultantId,
                  user_id: session.user.id,
                  full_name: chosen.fullName,
                  role: chosen.role,
                  firm_id: chosen.firmId,
                  firm_name: chosen.firmName,
                  carteira_id: chosen.carteiraId,
                }
              : {
                  id: session.user.id,
                  user_id: session.user.id,
                  full_name: session.user.email ?? 'Plataforma',
                  role: 'admin' as Role,
                  firm_id: chosen.firmId,
                  firm_name: chosen.firmName,
                  carteira_id: null,
                },
        })
        return
      }

      // CLIENT path — magic-link only (no TOTP).
      const identity = await fetchClientIdentity(session.user.id)
      if (cancelled) return
      setState({
        status: 'authenticated_client',
        user: session.user,
        session,
        principals: list,
        client: {
          email: identity?.email ?? session.user.email ?? '',
          phone: identity?.phone ?? null,
          companies: chosen.companies,
        },
      })
    }

    supabase.auth.getSession().then(({ data }) => hydrate(data.session))
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      hydrate(session)
    })

    return () => {
      cancelled = true
      sub.subscription.unsubscribe()
    }
  }, [nonce])

  const switchTo = useCallback(
    async (p: Principal) => {
      const { error } =
        p.kind === 'staff'
          ? await supabase.rpc(
              'switch_principal' as never,
              {
                p_consultant_id: p.consultantId,
              } as never,
            )
          : await supabase.rpc('switch_client_context' as never, { p_firm_id: p.firmId } as never)
      if (error) {
        console.error('[useSession] switch failed', error.message)
        return false
      }
      setState({ status: 'loading' })
      refresh()
      return true
    },
    [refresh],
  )

  const value = useMemo<SessionContextValue>(
    () => ({ state, principals, active, isPlatformAdmin, switchTo, refresh }),
    [state, principals, active, isPlatformAdmin, switchTo, refresh],
  )
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

// Backwards-compatible: every existing consumer reads just the state.
export function useSession(): SessionState {
  const ctx = useContext(SessionContext)
  return ctx?.state ?? { status: 'loading' }
}

// For the switcher UI.
export function usePrincipals(): {
  principals: Principal[]
  active: Principal | null
  isPlatformAdmin: boolean
  switchTo: (p: Principal) => Promise<boolean>
} {
  const ctx = useContext(SessionContext)
  return {
    principals: ctx?.principals ?? [],
    active: ctx?.active ?? null,
    isPlatformAdmin: ctx?.isPlatformAdmin ?? false,
    switchTo: ctx?.switchTo ?? (async () => false),
  }
}
